import { useEffect, useState } from 'react';
import { Snackbar, Alert, AlertTitle, Avatar, Typography, Box, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../services/NotificationProvider';
import { getAppTheme } from '../../services/authContext'; 
import { useSelector } from 'react-redux';

const GlobalToastNotification = () => {
  const notificationContext = useNotification();
  
  const {
    pendingChatToasts = [],
    pendingSystemToasts = [],
    consumeToast, // Use the generic consumeToast
    // setCurrentConversation, // Available if needed
  } = notificationContext || {};
  const navigate = useNavigate();
  const mode = useSelector((state) => state.theme.mode);
  const theme = getAppTheme(mode);

  const [currentToast, setCurrentToast] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      if (Array.isArray(pendingSystemToasts) && pendingSystemToasts.length > 0) {
        setCurrentToast({ ...pendingSystemToasts[0], toastType: 'system' });
        setOpen(true);
      } else if (Array.isArray(pendingChatToasts) && pendingChatToasts.length > 0) {
        setCurrentToast({ ...pendingChatToasts[0], toastType: 'chat' });
        setOpen(true);
      }
    }
  }, [pendingChatToasts, pendingSystemToasts, open]);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpen(false);
  };

  const handleExited = () => {
    if (currentToast) {
      // Use the generic consumeToast with type
      consumeToast(currentToast.id, currentToast.toastType);
      setCurrentToast(null); 
    }
  };

  const handleToastClick = () => {
    if (currentToast) {
      if (currentToast.toastType === 'system' && currentToast.deep_link) {
        navigate(currentToast.deep_link);
      } else if (currentToast.toastType === 'chat' && currentToast.conversation_id) {
        navigate(`/chat?conversationId=${currentToast.conversation_id}`);
      }
      setOpen(false); 
    }
  };

  if (!currentToast) {
    return null;
  }

  let title = currentToast.title;
  let message = currentToast.message;
  let avatarSrc = currentToast.sender_avatar; // Default for chat
  let avatarChar = (currentToast.sender || 'N').charAt(0).toUpperCase();
  let severity = 'info';

  if (currentToast.toastType === 'system') {
    title = currentToast.title || 'System Notification';
    avatarSrc = currentToast.icon; // Use system notification icon if available
    if (currentToast.type === 'match') {
      title = 'New Match!';
      const matchedUserName = currentToast.metadata?.matched_user_name || 'someone';
      message = `You've matched with ${matchedUserName}!`;
      avatarSrc = currentToast.metadata?.matched_user_profile_picture || currentToast.icon;
      avatarChar = (matchedUserName).charAt(0).toUpperCase();
      severity = 'success';
    } else {
      severity = 'info'; // Default for other system notifications
    }
  } else { // Chat toast
    title = currentToast.sender || 'New Message';
    avatarChar = (currentToast.sender || 'U').charAt(0).toUpperCase();
  }
  message = message || (currentToast.toastType === 'system' ? 'You have a new notification.' : 'You have a new message.');


  return (
    <Snackbar
      open={open}
      autoHideDuration={6000}
      onClose={handleClose}
      TransitionProps={{ onExited: handleExited }}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 7, zIndex: (theme) => theme.zIndex.snackbar + 100 }}
    >
      <Alert
        onClose={handleClose}
        severity={severity}
        sx={{
          width: '100%',
          maxWidth: 380,
          boxShadow: theme.shadows[6],
          bgcolor: 'background.paper',
          '.MuiAlert-icon': { fontSize: '1.5rem', alignItems: 'center' },
          '.MuiAlert-message': { width: '100%' },
          cursor: (currentToast.deep_link || currentToast.conversation_id) ? 'pointer' : 'default',
        }}
        onClick={handleToastClick}
        icon={
            <Avatar 
                src={avatarSrc || undefined} // Ensure undefined if src is null/empty to show char
                sx={{ bgcolor: theme.palette.primary.main, width: 30, height: 30, color: theme.palette.primary.contrastText }}
            >
                {!avatarSrc && avatarChar}
            </Avatar>
        }
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Box>
                <AlertTitle sx={{ fontWeight: 'bold', mb: 0.5 }}>{title}</AlertTitle>
                <Typography variant="body2" sx={{ 
                    display: '-webkit-box', 
                    WebkitLineClamp: 2, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    mb: 0.5
                }}>
                    {message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {new Date(currentToast.timestamp || Date.now()).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
            </Box>
            <Tooltip title="Close">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleClose(); }} sx={{ position: 'absolute', top: 8, right: 8}}>
                    <CloseIcon fontSize="inherit" />
                </IconButton>
            </Tooltip>
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default GlobalToastNotification;
